import { BookOpen, Ban, Lock, X } from 'lucide-react';
import { BlockAppsCard } from './BlockApps';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Child, SiteRule } from '../lib/types';
import { useParent } from './ParentContext';
import { useAlsoFor } from './CopySettings';

const SUGGESTED_BLOCKS = ['youtube.com', 'roblox.com', 'tiktok.com', 'discord.com', 'netflix.com', 'twitch.tv', 'instagram.com', 'coolmathgames.com'];

/** Learning sites every student has (#181) — ClassGuardian itself, plus this server's own host when self-hosted. */
function builtInLearningSites(): string[] {
  const sites = ['classguardian.app'];
  const own = location.hostname.replace(/^www\./, '');
  // Show a self-hosted server's own domain too (the server allows it); skip localhost/IPs and classguardian.app subdomains.
  const isLocal = own === 'localhost' || /^[\d.]+$/.test(own);
  if (own && !isLocal && own !== 'classguardian.app' && !own.endsWith('.classguardian.app')) sites.push(own);
  return sites;
}

export function SiteRules({ child }: { child: Child }) {
  const { refresh, replaceChild, toast } = useParent();
  const [savingMode, setSavingMode] = useState(false);

  // "Only allow learning sites" (#175): block every other site during school time.
  async function setLearningOnly(on: boolean) {
    setSavingMode(true);
    try {
      replaceChild(await api.patch<Child>(`/api/children/${child.id}`, { learningOnly: on }));
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
    } finally {
      setSavingMode(false);
    }
  }

  const [rules, setRules] = useState<SiteRule[] | null>(null);
  const { also, picker } = useAlsoFor(child);

  useEffect(() => {
    api.get<SiteRule[]>(`/api/children/${child.id}/rules`).then(setRules);
  }, [child.id]);

  async function add(kind: SiteRule['kind'], pattern: string) {
    try {
      const rule = await api.post<SiteRule>(`/api/children/${child.id}/rules`, { kind, pattern });
      setRules((list) => (list?.some((r) => r.id === rule.id) ? list : [...(list ?? []), rule]));
      void also({ site: { kind, pattern } });
      refresh();
      return true;
    } catch (e) {
      toast({ text: (e as Error).message, tone: 'alert' });
      return false;
    }
  }

  async function remove(rule: SiteRule) {
    await api.del(`/api/rules/${rule.id}`);
    setRules((list) => list?.filter((r) => r.id !== rule.id) ?? null);
    refresh();
  }

  if (!rules) return <p className="muted">Loading…</p>;
  const blocked = rules.filter((r) => r.kind === 'block');
  const learning = rules.filter((r) => r.kind === 'learn');
  const hasExtension = child.devices.some((d) => d.kind === 'extension');

  return (
    <div className="stack">
      {picker}
      {!hasExtension && (
        <p className="notice">
          Website blocking and the website log need the ClassGuardian browser extension on {child.name}’s browser. See the Devices tab.
        </p>
      )}
      <RuleCard
        icon={<BookOpen size={18} />}
        title="Learning sites"
        hint={
          child.learningOnly
            ? 'During school time these are the only sites that open.'
            : 'Optional. When this list has sites, you get a focus alert if your child spends school time on any other site.'
        }
        rules={learning}
        fixed={builtInLearningSites()}
        fixedTitle="Always allowed — ClassGuardian itself (the student portal) is never counted as off-task"
        onAdd={(p) => add('learn', p)}
        onRemove={remove}
        suggestions={[]}
      />

      <section className="card stack">
        <label className="check">
          <input type="checkbox" checked={!!child.learningOnly} disabled={savingMode} onChange={(e) => setLearningOnly(e.target.checked)} />
          <span>
            <strong>Only allow learning sites during school time</strong>
            <span className="muted small block">
              Every other site is blocked (it shows a friendly page and sends you an alert). Turn this off to block just the sites you list
              instead.
            </span>
          </span>
        </label>
        {child.learningOnly && !learning.length && (
          <p className="notice">Add at least one learning site above — right now only ClassGuardian itself can be opened during school time.</p>
        )}
      </section>

      <BlockAppsCard child={child} also={(c) => void also(c)} />

      {!child.learningOnly && (
        <RuleCard
          icon={<Ban size={18} />}
          title="Blocked during school time"
          hint="These sites won’t open during school hours. Trying one shows a friendly page instead and sends you an alert."
          rules={blocked}
          onAdd={(p) => add('block', p)}
          onRemove={remove}
          suggestions={SUGGESTED_BLOCKS.filter((s) => !blocked.some((r) => r.pattern === s))}
        />
      )}
    </div>
  );
}

function RuleCard(props: {
  icon: React.ReactNode;
  title: string;
  hint: string;
  rules: SiteRule[];
  /** Built-in entries shown first, locked (can't be removed). */
  fixed?: string[];
  fixedTitle?: string;
  suggestions: string[];
  onAdd: (pattern: string) => Promise<boolean>;
  onRemove: (r: SiteRule) => void;
}) {
  const [text, setText] = useState('');
  return (
    <section className="card">
      <h2 className="with-icon">
        {props.icon} {props.title}
      </h2>
      <p className="muted small">{props.hint}</p>
      <form
        className="inline-form"
        onSubmit={async (e) => {
          e.preventDefault();
          if (text.trim() && (await props.onAdd(text))) setText('');
        }}
      >
        <input placeholder="example.com" value={text} onChange={(e) => setText(e.target.value)} inputMode="url" autoCapitalize="none" />
        <button className="btn primary" disabled={!text.trim()}>
          Add
        </button>
      </form>
      <div className="pills">
        {props.fixed?.map((f) => (
          <span key={`fixed-${f}`} className="pill fixed" title={props.fixedTitle}>
            <Lock size={12} /> {f}
          </span>
        ))}
        {props.rules.map((r) => (
          <span key={r.id} className="pill">
            {r.pattern}
            <button onClick={() => props.onRemove(r)} aria-label={`Remove ${r.pattern}`}>
              <X size={14} />
            </button>
          </span>
        ))}
        {!props.rules.length && !props.fixed?.length && <span className="muted small">None yet.</span>}
      </div>
      {props.suggestions.length > 0 && (
        <div className="suggest">
          <span className="muted small">Common picks:</span>
          {props.suggestions.map((s) => (
            <button key={s} className="chip" onClick={() => props.onAdd(s)}>
              + {s}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
